import React from 'react';
import { Step, Steps, Wizard, WizardContext } from 'react-albus';
import { useDispatch, useSelector } from 'react-redux';
import {
  setEditableAssetClasses,
  setEditableReturns,
  setEditableVolatilities,
} from '../../actions';
import {
  selectEditableAssetClasses,
  selectEditableReturns,
  selectEditableVolatilities,
} from '../../selector';
import { STEP_IDS } from './constants';
import { SideNavigation } from './SideNavigation';
import { EditCapitalMarketAssumptions } from './steps/EditCapitalMarketAssumptions';
import { ImportCorrelationMatrix } from './steps/ImportCorrelationMatrix';
import { NameCustomAssetClasses } from './steps/NameCustomAssetClasses';
import { SelectAssetClasses } from './steps/SelectAssetClasses';
import { Layout } from './styles';

interface EditCustomModelAssumptionsProps {
  onFinish: () => void;
}

const EditCustomModelAssumptions: React.FC<EditCustomModelAssumptionsProps> = ({ onFinish }) => {
  const dispatch = useDispatch();

  const editableAssetClasses = useSelector(selectEditableAssetClasses);
  const editableReturns = useSelector(selectEditableReturns);
  const editableVolatilities = useSelector(selectEditableVolatilities);

  const onNext = ({ step, steps, push }: WizardContext) => {
    const isLastStep = steps.length > 0 && steps[steps.length - 1].id === step.id;

    if (isLastStep) {
      onFinish();
    } else if (
      step.id === STEP_IDS.SELECT_ASSET_CLASSES &&
      editableAssetClasses.filter(({ included, isCustom }) => included && isCustom).length === 0
    ) {
      push(STEP_IDS.SET_RETURNS_AND_VOLATILITIES);
    } else {
      push();
    }
  };

  return (
    <Wizard
      onNext={onNext}
      render={wizard => (
        <Layout>
          <SideNavigation wizard={wizard} />

          <Steps>
            <Step id={STEP_IDS.SELECT_ASSET_CLASSES}>
              {({ next }) => (
                <SelectAssetClasses
                  assetClasses={editableAssetClasses}
                  next={updatedAssetClasses => {
                    dispatch(setEditableAssetClasses(updatedAssetClasses));
                    next();
                  }}
                />
              )}
            </Step>
            <Step id={STEP_IDS.NAME_CUSTOM_ASSET_CLASSES}>
              {({ next }) => (
                <NameCustomAssetClasses
                  assetClasses={editableAssetClasses}
                  next={updatedAssetClasses => {
                    dispatch(setEditableAssetClasses(updatedAssetClasses));
                    next();
                  }}
                />
              )}
            </Step>
            <Step id={STEP_IDS.SET_RETURNS_AND_VOLATILITIES}>
              {({ next }) => (
                <EditCapitalMarketAssumptions
                  returns={editableReturns}
                  volatilities={editableVolatilities}
                  assetClasses={editableAssetClasses}
                  next={(updatedReturns, updatedVolatilities) => {
                    dispatch(setEditableReturns(updatedReturns));
                    dispatch(setEditableVolatilities(updatedVolatilities));
                    next();
                  }}
                />
              )}
            </Step>
            <Step id={STEP_IDS.IMPORT_CORRELATION_MATRIX}>
              {({ next }) => (
                <ImportCorrelationMatrix
                  assetClasses={editableAssetClasses}
                  returns={editableReturns}
                  volatilities={editableVolatilities}
                  next={next}
                />
              )}
            </Step>
          </Steps>
        </Layout>
      )}
    />
  );
};

export default EditCustomModelAssumptions;
